/**
 * Voice-First JARVIS — Aurora-Orb + Continuous-Conversation.
 *
 * Architektur:
 *   Orb         (visuelle Repräsentation der Session)
 *   Transcript  (Chat-Verlauf, persistent)
 *   VoiceSession (orchestriert Mic → STT → /chat → TTS → Loop)
 */

import { changePassword, clearToken, getToken, login, setToken } from "./api";
import { Orb } from "./orb";
import { VoiceSession, type SessionConfig } from "./session";
import { Transcript } from "./transcript";
import { renderReplay, Sidebar } from "./sidebar";
import { UploadsPanel } from "./uploads-panel";

const VOICE_PREF_KEY = "jarvis.voice";
const AUTOLISTEN_PREF_KEY = "jarvis.autoListen";
const PTT_PREF_KEY = "jarvis.pushToTalk";
const MODE_PREF_KEY = "jarvis.mode";
const SPEAKER_MODE_PREF_KEY = "jarvis.speakerMode";
const REPLY_MODE_PREF_KEY = "jarvis.replyMode"; // "auto" | "voice" | "text"
const ONBOARDING_SEEN_KEY = "jarvis.onboarded.v6";

type ReplyMode = "auto" | "voice" | "text";
function readReplyMode(): ReplyMode {
  const raw = localStorage.getItem(REPLY_MODE_PREF_KEY);
  if (raw === "voice" || raw === "text" || raw === "auto") return raw;
  return "auto";
}
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

// Build-Marker: bei jedem Release manuell bumpen, damit Selman erkennt ob der
// Tunnel die neue Shell ausliefert. (Wird im Footer angezeigt.)
const BUILD_TAG = "v6 · light · realtime";

// Realtime-API-Voices (alloy/echo/shimmer/ash/ballad/coral/sage/verse).
// fable/nova/onyx sind nur im Classic-Mode (OpenAI Audio Speech API) verfügbar.
const REALTIME_VOICES = [
  { id: "alloy", label: "Alloy — neutral, ausgewogen" },
  { id: "ash", label: "Ash — sachlich" },
  { id: "ballad", label: "Ballad — ruhig, erzählerisch" },
  { id: "coral", label: "Coral — hell" },
  { id: "echo", label: "Echo — männlich, klar" },
  { id: "sage", label: "Sage — bedacht" },
  { id: "shimmer", label: "Shimmer — freundlich" },
  { id: "verse", label: "Verse — lebhaft" },
];

const CLASSIC_VOICES = [
  { id: "fable", label: "Fable — warm, britisch (Classic)" },
  { id: "onyx", label: "Onyx — tief (Classic)" },
  { id: "ash", label: "Ash — sachlich" },
  { id: "verse", label: "Verse — lebhaft" },
  { id: "sage", label: "Sage — bedacht" },
  { id: "coral", label: "Coral — hell" },
  { id: "nova", label: "Nova — freundlich (Classic)" },
];

function voicesForMode(mode: SessionConfig["mode"]): { id: string; label: string }[] {
  return mode === "realtime" ? REALTIME_VOICES : CLASSIC_VOICES;
}

function readConfig(): SessionConfig {
  const modeRaw = localStorage.getItem(MODE_PREF_KEY);
  const mode: SessionConfig["mode"] = modeRaw === "classic" ? "classic" : "realtime";
  const fallbackVoice = mode === "realtime" ? "alloy" : "fable";
  const voice = localStorage.getItem(VOICE_PREF_KEY) || fallbackVoice;
  const autoListenRaw = localStorage.getItem(AUTOLISTEN_PREF_KEY);
  const autoListen = autoListenRaw === null ? true : autoListenRaw === "1";
  const pttRaw = localStorage.getItem(PTT_PREF_KEY);
  const pushToTalk = pttRaw === "1";
  const speakerMode = localStorage.getItem(SPEAKER_MODE_PREF_KEY) === "1";
  return { voice, autoListen, pushToTalk, mode, speakerMode };
}

function persistConfig(cfg: SessionConfig): void {
  localStorage.setItem(VOICE_PREF_KEY, cfg.voice);
  localStorage.setItem(AUTOLISTEN_PREF_KEY, cfg.autoListen ? "1" : "0");
  localStorage.setItem(PTT_PREF_KEY, cfg.pushToTalk ? "1" : "0");
  localStorage.setItem(MODE_PREF_KEY, cfg.mode);
  localStorage.setItem(SPEAKER_MODE_PREF_KEY, cfg.speakerMode ? "1" : "0");
}

const app = document.getElementById("app")!;

function render(): void {
  if (!getToken()) renderLogin();
  else renderMain();
}

// ── Login ───────────────────────────────────────────────────────────────────
function renderLogin(): void {
  app.innerHTML = `
    <main class="login-stage">
      <div class="aurora-bg" aria-hidden="true">
        <div class="aurora aurora-a"></div>
        <div class="aurora aurora-b"></div>
        <div class="aurora aurora-c"></div>
        <div class="aurora aurora-d"></div>
        <div class="grain" aria-hidden="true"></div>
      </div>
      <form class="login-card glass" id="loginForm" autocomplete="on" novalidate>
        <div class="card-glow"></div>
        <div class="brand">
          <span class="brand-mark">veloro</span>
          <h1 class="brand-title">JARVIS</h1>
          <p class="brand-sub">Selmans persönlicher KI-Begleiter</p>
        </div>
        <label class="field">
          <span class="field-label">E-Mail</span>
          <input id="email" type="email" inputmode="email" placeholder="dein@email.com" autocomplete="username" required value="office@veloro.at" />
        </label>
        <label class="field">
          <span class="field-label">Passwort</span>
          <input id="password" type="password" placeholder="••••••••" autocomplete="current-password" required />
        </label>
        <button type="submit" class="submit" id="loginBtn">
          <span class="submit-label">Anmelden</span>
          <span class="submit-arrow" aria-hidden="true">→</span>
          <span class="submit-shimmer" aria-hidden="true"></span>
        </button>
        <div class="err-toast" id="loginErr" hidden></div>
        <p class="login-footer">verschlüsselt · privat · nur für dich</p>
      </form>
      <span class="build-tag" aria-hidden="true">${BUILD_TAG}</span>
    </main>
  `;

  const form = document.getElementById("loginForm") as HTMLFormElement;
  const btn = document.getElementById("loginBtn") as HTMLButtonElement;
  const err = document.getElementById("loginErr") as HTMLDivElement;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    err.hidden = true;
    btn.disabled = true;
    btn.classList.add("is-busy");
    try {
      const email = (document.getElementById("email") as HTMLInputElement).value.trim();
      const password = (document.getElementById("password") as HTMLInputElement).value;
      const token = await login(email, password);
      setToken(token);
      render();
    } catch (e2) {
      err.hidden = false;
      err.textContent = e2 instanceof Error ? e2.message : String(e2);
      btn.disabled = false;
      btn.classList.remove("is-busy");
    }
  });
}

// ── Main / Voice-First Hero ─────────────────────────────────────────────────
function renderMain(): void {
  app.innerHTML = `
    <main class="stage">
      <div class="aurora-bg" aria-hidden="true">
        <div class="aurora aurora-a"></div>
        <div class="aurora aurora-b"></div>
        <div class="aurora aurora-c"></div>
        <div class="aurora aurora-d"></div>
        <div class="grain" aria-hidden="true"></div>
      </div>

      <header class="top">
        <div class="top-brand">
          <span class="brand-mark">veloro</span>
          <span class="brand-sep">·</span>
          <span class="brand-title-sm">jarvis</span>
        </div>
        <div class="top-actions">
          <button type="button" class="icon-btn icon-btn-square" id="imageUploadBtn" aria-label="Bild an JARVIS senden">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          </button>
          <button type="button" class="icon-btn icon-btn-square" id="uploadsBtn" aria-label="Bild-Uploads vom Handy">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="8.5" cy="11" r="1.5"/><path d="m21 17-5-5L5 19"/></svg>
          </button>
          <button type="button" class="icon-btn" id="muteBtn" hidden aria-label="Mikrofon stummschalten" aria-pressed="false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="mute-icon-on"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="mute-icon-off" style="display:none"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
            <span id="muteLabel">Mute</span>
          </button>
          <button type="button" class="icon-btn" id="stopBtn" hidden aria-label="Session beenden">
            <span class="stop-square" aria-hidden="true"></span>
            <span>Beenden</span>
          </button>
          <button type="button" class="icon-btn icon-btn-square" id="settingsBtn" aria-label="Einstellungen">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
        </button>
        </div>
      </header>

      <section class="hero" id="hero">
        <!-- Orb wird hier injiziert -->
      </section>

      <section class="image-drop glass" id="imageDropZone" aria-label="Bild-Upload">
        <p class="image-drop-title">Bild hierher ziehen oder hochladen</p>
        <p class="image-drop-hint">JPEG, PNG, WebP · max 8MB</p>
      </section>

      <section class="transcript-shell glass" id="transcriptShell">
        <!-- Live-Transcript (Voice/Text-Session) ODER Replay einer alten
             Vault-Session — wird beim Klick auf eine Sidebar-Session
             swap-overridden, "Neue Session" stellt das Live-Transcript wieder her. -->
      </section>

      <form class="composer glass" id="composerForm" autocomplete="off">
        <div class="composer-glow" aria-hidden="true"></div>
        <div class="composer-mode" role="group" aria-label="Antwort-Modus">
          <button type="button" class="composer-mode-btn" data-mode="auto" id="modeAuto" aria-pressed="false">Auto</button>
          <button type="button" class="composer-mode-btn" data-mode="voice" id="modeVoice" aria-pressed="false" title="JARVIS antwortet als Sprache">Voice</button>
          <button type="button" class="composer-mode-btn" data-mode="text" id="modeText" aria-pressed="false" title="JARVIS antwortet als Text">Text</button>
        </div>
        <textarea
          id="composerInput"
          class="composer-input"
          rows="1"
          placeholder="Tippe — oder tap den Orb zum Sprechen"
          aria-label="Nachricht an JARVIS"
          enterkeyhint="send"
          autocapitalize="sentences"
          autocomplete="off"
          spellcheck="true"
        ></textarea>
        <button type="submit" class="composer-send" id="composerSend" aria-label="Senden" disabled>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
        </button>
      </form>

      <input id="imageUploadInput" type="file" accept="image/jpeg,image/png,image/webp" hidden />

      <span class="build-tag" aria-hidden="true">${BUILD_TAG}</span>

      <div class="toast" id="toast" hidden></div>

      <aside class="uploads-panel" id="uploadsPanel" hidden aria-label="Bild-Uploads"></aside>

      <div class="settings-panel" id="settingsPanel" hidden>
        <div class="settings-card glass" role="dialog" aria-labelledby="settingsTitle">
          <header class="settings-head">
            <h3 id="settingsTitle">Einstellungen</h3>
            <button type="button" class="icon-btn ghost" id="settingsClose" aria-label="Schließen">✕</button>
          </header>

          <section class="settings-section">
            <h4 class="settings-section-title">Interaktion</h4>
            <label class="toggle-row">
              <span class="toggle-row-body">
                <span class="field-label">Classic-Mode (Whisper)</span>
                <span class="field-hint">Off = OpenAI Realtime (sub-Sekunden-Latenz, wie ChatGPT). On = klassische Whisper→Chat→TTS-Pipeline.</span>
              </span>
              <span class="toggle">
                <input type="checkbox" id="classicModeToggle" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </span>
            </label>
            <label class="toggle-row">
              <span class="toggle-row-body">
                <span class="field-label">Lautsprecher-Modus</span>
                <span class="field-hint">An, wenn du übers iPhone-/Mac-Lautsprecher redest (statt AirPods/Headset). Half-Duplex unterdrückt den Echo-Loop — JARVIS hört dich erst wieder, wenn er ausgeredet hat.</span>
              </span>
              <span class="toggle">
                <input type="checkbox" id="speakerModeToggle" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </span>
            </label>
            <label class="toggle-row">
              <span class="toggle-row-body">
                <span class="field-label">Push-to-Talk</span>
                <span class="field-hint">Sicherer auf iOS: Orb gedrückt halten zum Sprechen, loslassen sendet. Nur Classic-Mode.</span>
              </span>
              <span class="toggle">
                <input type="checkbox" id="pttToggle" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </span>
            </label>
            <label class="toggle-row">
              <span class="toggle-row-body">
                <span class="field-label">Auto-Listen</span>
                <span class="field-hint">Nach JARVIS' Antwort automatisch wieder hineinhören. Nur Classic-Mode.</span>
              </span>
              <span class="toggle">
                <input type="checkbox" id="autoListenToggle" />
                <span class="toggle-track"><span class="toggle-thumb"></span></span>
              </span>
            </label>
          </section>

          <div class="settings-divider"></div>

          <section class="settings-section">
            <h4 class="settings-section-title">Stimme</h4>
            <label class="field">
              <span class="field-label sr-only">Stimme</span>
              <div class="select-wrap">
                <select id="voiceSelect" class="select" aria-label="Stimme"></select>
              </div>
            </label>
          </section>

          <div class="settings-divider"></div>

          <section class="settings-section">
            <h4 class="settings-section-title">Sicherheit</h4>
            <form id="passwordForm" class="password-form" autocomplete="off" novalidate>
              <label class="field">
                <span class="field-label">Aktuelles Passwort</span>
                <input id="pwCurrent" type="password" autocomplete="current-password" required />
              </label>
              <label class="field">
                <span class="field-label">Neues Passwort</span>
                <input id="pwNew" type="password" autocomplete="new-password" minlength="12" required />
              </label>
              <label class="field">
                <span class="field-label">Neues Passwort wiederholen</span>
                <input id="pwRepeat" type="password" autocomplete="new-password" minlength="12" required />
              </label>
              <p class="field-hint">Mindestens 12 Zeichen. Update läuft im Hintergrund — kein Server-Restart nötig.</p>
              <div class="password-row">
                <button type="submit" class="ghost-btn" id="pwSubmit">Passwort ändern</button>
                <span class="password-status" id="pwStatus" role="status" aria-live="polite"></span>
              </div>
            </form>
          </section>

          <div class="settings-divider"></div>

          <section class="settings-section settings-actions">
            <button type="button" class="ghost-btn" id="clearTranscript">Transkript leeren</button>
            <button type="button" class="ghost-btn danger" id="logoutBtn">Abmelden</button>
          </section>
        </div>
      </div>
    </main>
  `;

  const hero = document.getElementById("hero")!;
  const transcriptShell = document.getElementById("transcriptShell")!;
  const stage = document.querySelector(".stage") as HTMLElement;
  const topActions = document.querySelector(".top-actions") as HTMLElement;
  const imageUploadBtn = document.getElementById("imageUploadBtn") as HTMLButtonElement;
  const imageUploadInput = document.getElementById("imageUploadInput") as HTMLInputElement;
  const imageDropZone = document.getElementById("imageDropZone") as HTMLDivElement;
  const stopBtn = document.getElementById("stopBtn") as HTMLButtonElement;
  const muteBtn = document.getElementById("muteBtn") as HTMLButtonElement;
  const muteIconOn = muteBtn?.querySelector(".mute-icon-on") as SVGElement | null;
  const muteIconOff = muteBtn?.querySelector(".mute-icon-off") as SVGElement | null;
  const muteLabel = document.getElementById("muteLabel") as HTMLSpanElement | null;
  const settingsBtn = document.getElementById("settingsBtn") as HTMLButtonElement;
  const settingsPanel = document.getElementById("settingsPanel") as HTMLDivElement;
  const settingsClose = document.getElementById("settingsClose") as HTMLButtonElement;
  const voiceSelect = document.getElementById("voiceSelect") as HTMLSelectElement;
  const autoListenToggle = document.getElementById("autoListenToggle") as HTMLInputElement;
  const pttToggle = document.getElementById("pttToggle") as HTMLInputElement;
  const classicModeToggle = document.getElementById("classicModeToggle") as HTMLInputElement;
  const speakerModeToggle = document.getElementById("speakerModeToggle") as HTMLInputElement;
  const logoutBtn = document.getElementById("logoutBtn") as HTMLButtonElement;
  const clearBtn = document.getElementById("clearTranscript") as HTMLButtonElement;
  const toastEl = document.getElementById("toast") as HTMLDivElement;
  const uploadsPanelEl = document.getElementById("uploadsPanel") as HTMLElement;
  const uploadsBtn = document.getElementById("uploadsBtn") as HTMLButtonElement;

  const cfg = readConfig();

  function refreshVoiceList(): void {
    const opts = voicesForMode(session.getConfig().mode);
    voiceSelect.innerHTML = opts.map((o) => `<option value="${o.id}">${o.label}</option>`).join("");
    const desired = session.getConfig().voice;
    const match = opts.find((o) => o.id === desired);
    voiceSelect.value = match ? desired : opts[0]?.id ?? "";
    if (!match && opts[0]) session.updateConfig({ voice: opts[0].id });
  }

  autoListenToggle.checked = cfg.autoListen;
  pttToggle.checked = cfg.pushToTalk;
  classicModeToggle.checked = cfg.mode === "classic";
  speakerModeToggle.checked = cfg.speakerMode;

  const transcript = new Transcript(transcriptShell);
  // Replay-Container als Sibling zur Transcript-Liste im gleichen Shell.
  // So bleibt die Transcript-Instanz (DOM + LocalStorage-Persistenz) intakt
  // wenn wir zwischen Replay und Live hin und her wechseln.
  const replayContainer = document.createElement("div");
  replayContainer.className = "replay-container";
  replayContainer.hidden = true;
  transcriptShell.appendChild(replayContainer);
  const transcriptRootEl = transcript.root;
  let inReplay = false;

  // ── Sidebar: Session-Verlauf (ChatGPT-Style, links) ───────────────────────
  const sidebar = new Sidebar(stage, {
    onNewSession: () => {
      leaveReplay();
    },
    onSelectSession: (file, md) => {
      enterReplay();
      renderReplay(replayContainer, file, md);
    },
    onLeaveReplay: () => {
      if (inReplay) leaveReplay();
    },
  });
  // Hamburger-Toggle als ERSTES Element in den top-actions (links neben den anderen Icons).
  topActions.insertBefore(sidebar.getToggleButton(), topActions.firstChild);
  // Preload Session-Liste (nicht-blockierend, erscheint sobald Sidebar geöffnet wird).
  void sidebar.refresh();

  function enterReplay(): void {
    if (inReplay) return;
    inReplay = true;
    document.body.classList.add("is-replay");
    transcriptRootEl.hidden = true;
    replayContainer.hidden = false;
    // Live-Session beenden, falls aktiv — Replay ist read-only.
    if (session?.isActive()) session.stop();
  }

  function leaveReplay(): void {
    if (!inReplay) return;
    inReplay = false;
    document.body.classList.remove("is-replay");
    replayContainer.hidden = true;
    replayContainer.innerHTML = "";
    transcriptRootEl.hidden = false;
  }

  const orb = new Orb(hero, {
    onTap: () => {
      session.toggle().catch((err) => {
        console.error("Session-Toggle-Fehler:", err);
      });
    },
    onPressStart: () => {
      session.beginPushToTalk().catch((err) => {
        console.error("PTT-Start-Fehler:", err);
      });
    },
    onPressEnd: () => {
      session.endPushToTalk();
    },
    onPressCancel: () => {
      session.cancelPushToTalk();
    },
  });
  orb.setMode(cfg.pushToTalk ? "ptt" : "tap");

  const session = new VoiceSession({
    orb,
    transcript,
    config: cfg,
    onActiveChange: (active) => {
      stopBtn.hidden = !active;
      if (muteBtn) {
        muteBtn.hidden = !active;
        if (!active) {
          // Reset mute state when session ends
          muteBtn.setAttribute("aria-pressed", "false");
          if (muteIconOn) muteIconOn.style.display = "";
          if (muteIconOff) muteIconOff.style.display = "none";
          if (muteLabel) muteLabel.textContent = "Mute";
          muteBtn.classList.remove("is-muted");
        }
      }
    },
    onConfigChange: (next) => persistConfig(next),
  });

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
      reader.onload = () => {
        const result = typeof reader.result === "string" ? reader.result : "";
        if (!result.startsWith("data:")) {
          reject(new Error("Ungültiger Dateiinhalt."));
          return;
        }
        resolve(result);
      };
      reader.readAsDataURL(file);
    });

  const uploadImage = async (file: File) => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      showToast("Nur JPEG, PNG oder WebP erlaubt");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      showToast("Bild ist zu groß (max 8MB)");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      await session.sendImage(dataUrl, "Was siehst du auf diesem Bild?", file.name);
      showToast("Bild gesendet");
    } catch (err) {
      console.error("[jarvis] image upload:", err);
      showToast(err instanceof Error ? err.message : "Bild konnte nicht gesendet werden");
    }
  };

  // Settings
  const openSettings = () => {
    settingsPanel.hidden = false;
    requestAnimationFrame(() => settingsPanel.classList.add("is-open"));
  };
  const closeSettings = () => {
    settingsPanel.classList.remove("is-open");
    setTimeout(() => {
      settingsPanel.hidden = true;
    }, 240);
  };
  settingsBtn.addEventListener("click", openSettings);
  settingsClose.addEventListener("click", closeSettings);
  settingsPanel.addEventListener("click", (e) => {
    if (e.target === settingsPanel) closeSettings();
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !settingsPanel.hidden) closeSettings();
  });

  imageUploadBtn.addEventListener("click", () => {
    imageUploadInput.click();
  });

  // Eigenständiges Bild-Upload-Panel (HTTP-Upload mit Server-Storage + Thumbnails).
  // Anders als imageUploadBtn (data-URL → Chat), wird hier wirklich auf den Server geschoben.
  const uploadsPanel = new UploadsPanel(uploadsPanelEl, showToast);
  uploadsBtn.addEventListener("click", () => uploadsPanel.show());
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !uploadsPanelEl.hidden) uploadsPanel.hide();
  });
  uploadsPanelEl.addEventListener("click", (e) => {
    if (e.target === uploadsPanelEl) uploadsPanel.hide();
  });
  imageUploadInput.addEventListener("change", () => {
    const file = imageUploadInput.files?.[0];
    imageUploadInput.value = "";
    if (!file) return;
    void uploadImage(file);
  });

  imageDropZone.addEventListener("dragover", (e) => {
    e.preventDefault();
    imageDropZone.classList.add("is-dragover");
  });
  imageDropZone.addEventListener("dragenter", (e) => {
    e.preventDefault();
    imageDropZone.classList.add("is-dragover");
  });
  imageDropZone.addEventListener("dragleave", (e) => {
    e.preventDefault();
    if (e.target === imageDropZone) imageDropZone.classList.remove("is-dragover");
  });
  imageDropZone.addEventListener("drop", (e) => {
    e.preventDefault();
    imageDropZone.classList.remove("is-dragover");
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    void uploadImage(file);
  });

  // Voice-Liste an Mode anpassen.
  refreshVoiceList();

  voiceSelect.addEventListener("change", () => {
    session.updateConfig({ voice: voiceSelect.value });
    showToast(`Stimme: ${voiceSelect.options[voiceSelect.selectedIndex]?.text ?? voiceSelect.value}`);
  });
  autoListenToggle.addEventListener("change", () => {
    session.updateConfig({ autoListen: autoListenToggle.checked });
  });
  pttToggle.addEventListener("change", () => {
    const enabled = pttToggle.checked;
    session.updateConfig({ pushToTalk: enabled });
    orb.setMode(enabled ? "ptt" : "tap");
    if (enabled && session.getConfig().mode === "realtime") {
      // PTT macht im Realtime-Mode keinen Sinn (Server-VAD übernimmt). Wir
      // speichern die Preference trotzdem — wenn Selman in Classic-Mode
      // wechselt, ist sie aktiv.
      showToast("PTT gespeichert — aktiv erst im Classic-Mode");
    } else {
      showToast(enabled ? "Push-to-Talk aktiv — halten zum Sprechen" : "Tap-Modus aktiv");
    }
  });
  speakerModeToggle.addEventListener("change", () => {
    const enabled = speakerModeToggle.checked;
    session.updateConfig({ speakerMode: enabled });
    showToast(
      enabled
        ? "Lautsprecher-Modus an — JARVIS pausiert dein Mic während er spricht"
        : "Lautsprecher-Modus aus — Full-Duplex (für AirPods/Headset)",
    );
  });
  classicModeToggle.addEventListener("change", () => {
    const isClassic = classicModeToggle.checked;
    const nextMode: SessionConfig["mode"] = isClassic ? "classic" : "realtime";
    // Voice ggf. auf eine im neuen Mode unterstützte Stimme migrieren.
    const opts = voicesForMode(nextMode);
    const currentVoice = session.getConfig().voice;
    const stillValid = opts.some((o) => o.id === currentVoice);
    const nextVoice = stillValid ? currentVoice : opts[0]?.id ?? "alloy";
    // PTT im Realtime-Mode hart aus.
    if (nextMode === "realtime" && session.getConfig().pushToTalk) {
      pttToggle.checked = false;
      session.updateConfig({ pushToTalk: false });
      orb.setMode("tap");
    }
    session.updateConfig({ mode: nextMode, voice: nextVoice });
    refreshVoiceList();
    showToast(
      isClassic
        ? "Classic-Mode (Whisper) — fallback aktiv"
        : "Realtime aktiv — wie ChatGPT Advanced Voice",
    );
  });
  clearBtn.addEventListener("click", () => {
    transcript.clear();
    closeSettings();
  });

  const pwForm = document.getElementById("passwordForm") as HTMLFormElement;
  const pwCurrent = document.getElementById("pwCurrent") as HTMLInputElement;
  const pwNew = document.getElementById("pwNew") as HTMLInputElement;
  const pwRepeat = document.getElementById("pwRepeat") as HTMLInputElement;
  const pwSubmit = document.getElementById("pwSubmit") as HTMLButtonElement;
  const pwStatus = document.getElementById("pwStatus") as HTMLSpanElement;

  function setPwStatus(text: string, tone: "" | "ok" | "err"): void {
    pwStatus.textContent = text;
    pwStatus.classList.remove("is-ok", "is-err");
    if (tone === "ok") pwStatus.classList.add("is-ok");
    else if (tone === "err") pwStatus.classList.add("is-err");
  }

  pwForm.addEventListener("submit", async (e) => {
    e.preventDefault();
    const current = pwCurrent.value;
    const next = pwNew.value;
    const repeat = pwRepeat.value;
    if (!current) {
      setPwStatus("Aktuelles Passwort fehlt.", "err");
      return;
    }
    if (next.length < 12) {
      setPwStatus("Neues Passwort braucht mind. 12 Zeichen.", "err");
      return;
    }
    if (next !== repeat) {
      setPwStatus("Wiederholung stimmt nicht überein.", "err");
      return;
    }
    if (next === current) {
      setPwStatus("Neues Passwort muss sich vom aktuellen unterscheiden.", "err");
      return;
    }
    pwSubmit.disabled = true;
    setPwStatus("Speichere …", "");
    try {
      await changePassword(current, next);
      pwForm.reset();
      setPwStatus("", "");
      showToast("Passwort geändert");
      closeSettings();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPwStatus(msg, "err");
    } finally {
      pwSubmit.disabled = false;
    }
  });
  logoutBtn.addEventListener("click", () => {
    session.stop();
    clearToken();
    render();
  });

  stopBtn.addEventListener("click", () => {
    session.stop();
  });

  // Composer / Text-Input — Selman kann tippen statt sprechen.
  const composerForm = document.getElementById("composerForm") as HTMLFormElement;
  const composerInput = document.getElementById("composerInput") as HTMLTextAreaElement;
  const composerSend = document.getElementById("composerSend") as HTMLButtonElement;

  // ── Reply-Mode (Auto/Voice/Text) — Composer-Toggle ─────────────────────────
  // auto : tippen → Text-Antwort, sprechen → Sprach-Antwort (Default)
  // voice: alles → Sprach-Antwort
  // text : alles → Text-Antwort (kein Audio-Output)
  let replyMode: ReplyMode = readReplyMode();
  const modeBtns = Array.from(
    document.querySelectorAll<HTMLButtonElement>(".composer-mode-btn"),
  );
  function applyReplyModeUI(): void {
    for (const b of modeBtns) {
      const isActive = b.dataset.mode === replyMode;
      b.classList.toggle("is-active", isActive);
      b.setAttribute("aria-pressed", String(isActive));
    }
  }
  applyReplyModeUI();
  for (const b of modeBtns) {
    b.addEventListener("click", () => {
      const next = (b.dataset.mode || "auto") as ReplyMode;
      if (next === replyMode) return;
      replyMode = next;
      localStorage.setItem(REPLY_MODE_PREF_KEY, replyMode);
      applyReplyModeUI();
      const label = replyMode === "auto" ? "Auto" : replyMode === "voice" ? "Voice" : "Text";
      showToast(`Antwort-Modus: ${label}`);
    });
  }
  /** Liefert true wenn die nächste getippte Nachricht text-only beantwortet werden soll. */
  function shouldReplyAsText(): boolean {
    if (replyMode === "text") return true;
    if (replyMode === "auto") return true; // Auto: tippen → Text
    return false; // voice
  }

  const refreshSendEnabled = () => {
    composerSend.disabled = composerInput.value.trim().length === 0;
  };
  refreshSendEnabled();

  // Auto-grow Textarea — wächst bis ~5 Zeilen, dann scroll.
  const autoGrow = () => {
    composerInput.style.height = "auto";
    const next = Math.min(composerInput.scrollHeight, 132);
    composerInput.style.height = next + "px";
  };

  composerInput.addEventListener("input", () => {
    refreshSendEnabled();
    autoGrow();
  });

  // Enter → senden (Shift+Enter = neue Zeile).
  composerInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      composerForm.requestSubmit();
    }
  });

  const submitText = async () => {
    const text = composerInput.value.trim();
    if (!text) return;
    composerInput.value = "";
    refreshSendEnabled();
    autoGrow();
    try {
      await session.sendText(text, { textOnlyReply: shouldReplyAsText() });
    } catch (err) {
      console.error("[jarvis] sendText:", err);
      showToast("Konnte Text nicht senden");
    }
  };

  composerForm.addEventListener("submit", (e) => {
    e.preventDefault();
    void submitText();
  });

  // Mute-Toggle — Mikro temporär aus ohne Session zu beenden.
  muteBtn?.addEventListener("click", () => {
    const next = !session.isMuted();
    session.setMuted(next);
    muteBtn.setAttribute("aria-pressed", String(next));
    muteBtn.classList.toggle("is-muted", next);
    if (muteIconOn) muteIconOn.style.display = next ? "none" : "";
    if (muteIconOff) muteIconOff.style.display = next ? "" : "none";
    if (muteLabel) muteLabel.textContent = next ? "Unmute" : "Mute";
    muteBtn.setAttribute(
      "aria-label",
      next ? "Mikrofon aktivieren" : "Mikrofon stummschalten",
    );
  });

  // Onboarding: einmaligen Hinweis-Toast beim ersten Aufruf zeigen.
  if (!localStorage.getItem(ONBOARDING_SEEN_KEY)) {
    setTimeout(() => {
      const voiceMsg = cfg.mode === "realtime"
        ? "Tap auf den Orb für Live-Konversation"
        : cfg.pushToTalk
          ? "Halte den Orb zum Sprechen"
          : "Tap auf den Orb zum Sprechen";
      showToast(`${voiceMsg} — oder tippe unten direkt in die Box.`, 6000);
      localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    }, 800);
  }

  function showToast(text: string, ms = 2800): void {
    toastEl.textContent = text;
    toastEl.hidden = false;
    requestAnimationFrame(() => toastEl.classList.add("is-visible"));
    window.clearTimeout((toastEl as any)._tid);
    (toastEl as any)._tid = window.setTimeout(() => {
      toastEl.classList.remove("is-visible");
      setTimeout(() => {
        toastEl.hidden = true;
      }, 240);
    }, ms);
  }
}

// Service-Worker
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

render();
